import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingScreen } from "./auth";
import { AssessmentQuestionField, EmptyState, WorkspaceHeader } from "./common";
import { api, isPapiAssignment, type AssessmentAssignment } from "./core";

const PAPI_TIMER_STORAGE_KEY_PREFIX = "mothersense:papi-start:";

export function AssessmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<AssessmentAssignment | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [profile, setProfile] = useState<Record<string, string>>({
    fullName: "",
    birthDate: "",
    gender: "",
    education: "",
    position: "",
    industry: "",
    industryOther: "",
    tenureMonths: "",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef("");

  const load = async () => {
    const result = await api<{ assignment: AssessmentAssignment }>(`/api/assessments/${id}`);
    const nextAssignment = result.assignment;
    setAssignment(nextAssignment);

    const responseAnswers = Object.fromEntries((nextAssignment.response?.answers ?? []).map(answer => [answer.questionId, answer.value]));
    const draftAnswers = Object.fromEntries((nextAssignment.draft?.answers ?? []).map(answer => [answer.questionId, answer.value]));
    const nextAnswers = nextAssignment.status === "assigned" ? { ...responseAnswers, ...draftAnswers } : responseAnswers;

    const existingProfile = nextAssignment.response?.respondentProfile ?? nextAssignment.draft?.respondentProfile;
    const nextProfile = {
      fullName: existingProfile?.fullName ?? nextAssignment.order?.participantName ?? "",
      birthDate: existingProfile?.birthDate ?? "",
      gender: existingProfile?.gender ?? "",
      education: existingProfile?.education ?? "",
      position: existingProfile?.position ?? "",
      industry: existingProfile?.industry ?? "",
      industryOther: existingProfile?.industryOther ?? "",
      tenureMonths: existingProfile?.tenureMonths != null ? String(existingProfile.tenureMonths) : "",
    };

    setAnswers(nextAnswers);
    setProfile(nextProfile);
    setCurrentPage(0);
    lastSavedRef.current = serializePapiDraft(nextProfile, nextAnswers);
    hydratedRef.current = true;
  };

  useEffect(() => {
    load().catch(error => toast.error(error.message));
  }, [id]);

  useEffect(() => {
    if (!assignment || !isPapiAssignment(assignment) || assignment.status !== "assigned" || !hydratedRef.current) return;
    const payload = serializePapiDraft(profile, answers);
    if (payload === lastSavedRef.current) return;

    const timer = window.setTimeout(async () => {
      setSaveState("saving");
      try {
        await api(`/api/assessments/${assignment.id}/draft`, {
          method: "PUT",
          body: JSON.stringify(buildPapiDraftPayload(profile, answers)),
        });
        lastSavedRef.current = payload;
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [assignment, profile, answers]);

  useEffect(() => {
    if (!assignment || !isPapiAssignment(assignment) || assignment.status !== "assigned") {
      setRemainingSeconds(null);
      return;
    }

    const durationSeconds = (assignment.papiDefinition?.durationMinutes ?? 30) * 60;
    const storageKey = `${PAPI_TIMER_STORAGE_KEY_PREFIX}${assignment.id}`;
    const existingStartedAt = window.localStorage.getItem(storageKey);
    const startedAt = existingStartedAt ? Number(existingStartedAt) : Date.now();

    if (!existingStartedAt || Number.isNaN(startedAt)) {
      window.localStorage.setItem(storageKey, String(Date.now()));
    }

    const effectiveStartedAt = !existingStartedAt || Number.isNaN(startedAt) ? Date.now() : startedAt;

    const updateRemaining = () => {
      const elapsedSeconds = Math.floor((Date.now() - effectiveStartedAt) / 1000);
      setRemainingSeconds(Math.max(durationSeconds - elapsedSeconds, 0));
    };

    updateRemaining();
    const intervalId = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(intervalId);
  }, [assignment]);

  if (!assignment) return <LoadingScreen />;

  const questions = assignment.questions ?? [];
  const totalQuestionPages = questions.length;
  const totalSteps = isPapiAssignment(assignment) ? totalQuestionPages + 1 : 1;
  const currentQuestion = isPapiAssignment(assignment) ? questions[currentPage - 1] : null;

  const submitGeneric = async (event: FormEvent) => {
    event.preventDefault();
    if (questions.some(question => !answers[question.id])) {
      toast.error("Semua pertanyaan wajib dijawab");
      return;
    }

    try {
      await api(`/api/assessments/${assignment.id}/submit`, {
        method: "POST",
        body: JSON.stringify({ answers: questions.map(question => ({ questionId: question.id, value: answers[question.id] })) }),
      });
      toast.success("Jawaban terkirim untuk review psikolog");
      navigate("/portal");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal mengirim jawaban");
    }
  };

  const saveDraftNow = async () => {
    if (!isPapiAssignment(assignment) || assignment.status !== "assigned") return;
    const payload = serializePapiDraft(profile, answers);
    if (payload === lastSavedRef.current) return;
    setSaveState("saving");
    await api(`/api/assessments/${assignment.id}/draft`, {
      method: "PUT",
      body: JSON.stringify(buildPapiDraftPayload(profile, answers)),
    });
    lastSavedRef.current = payload;
    setSaveState("saved");
  };

  const nextStep = async () => {
    if (!isPapiAssignment(assignment)) return;
    if (currentPage === 0) {
      const identityError = validatePapiIdentity(profile);
      if (identityError) {
        toast.error(identityError);
        return;
      }
    } else {
      const question = questions[currentPage - 1];
      if (question && !answers[question.id]) {
        toast.error("Pilih salah satu pernyataan sebelum lanjut");
        return;
      }
    }
    try {
      await saveDraftNow();
    } catch {
      toast.error("Gagal menyimpan draft PAPI");
      return;
    }
    setCurrentPage(value => Math.min(value + 1, totalQuestionPages));
  };

  const previousStep = async () => {
    if (!isPapiAssignment(assignment)) return;
    try {
      await saveDraftNow();
    } catch {
      toast.error("Gagal menyimpan draft PAPI");
      return;
    }
    setCurrentPage(value => Math.max(value - 1, 0));
  };

  const submitPapi = async (event: FormEvent) => {
    event.preventDefault();
    const identityError = validatePapiIdentity(profile);
    if (identityError) {
      toast.error(identityError);
      setCurrentPage(0);
      return;
    }
    if (questions.some(question => !answers[question.id])) {
      toast.error("Semua pasangan pernyataan wajib dijawab");
      return;
    }

    try {
      await api(`/api/assessments/${assignment.id}/submit`, {
        method: "POST",
        body: JSON.stringify({
          respondentProfile: buildPapiSubmitProfile(profile),
          answers: questions.map(question => ({ questionId: question.id, value: answers[question.id] })),
        }),
      });
      window.localStorage.removeItem(`${PAPI_TIMER_STORAGE_KEY_PREFIX}${assignment.id}`);
      toast.success("Jawaban PAPI terkirim untuk review psikolog");
      navigate("/portal");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal mengirim jawaban");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <WorkspaceHeader title={assignment.template?.title ?? "Asesmen"} description={assignment.template?.instructions ?? ""} />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {assignment.status !== "assigned" ? (
          <Card className="rounded-lg">
            <CardContent className="pt-6">
              <EmptyState
                icon={CheckCircle2}
                title={assignment.status === "reviewed" ? "Laporan siap dilihat di portal" : "Jawaban sudah dikirim dan menunggu review"}
              />
            </CardContent>
          </Card>
        ) : isPapiAssignment(assignment) ? (
          <form onSubmit={submitPapi} className="grid gap-5">
            <Card className="rounded-lg">
              <CardHeader>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="font-display text-2xl text-primary">PAPI Kostick</CardTitle>
                    <CardDescription>
                      Langkah {currentPage + 1} dari {totalSteps}. Waktu pengerjaan {assignment.papiDefinition?.durationMinutes ?? 30} menit.
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {remainingSeconds != null && <Badge>Sisa waktu {formatRemainingTime(remainingSeconds)}</Badge>}
                    <Badge>
                      {saveState === "saving"
                        ? "Menyimpan..."
                        : saveState === "saved"
                          ? "Tersimpan"
                          : saveState === "error"
                            ? "Gagal menyimpan"
                            : "Siap"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-2 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((currentPage + 1) / totalSteps) * 100}%` }} />
                </div>
              </CardContent>
            </Card>

            {currentPage === 0 ? (
              <Card className="rounded-lg">
                <CardHeader>
                  <Badge>Langkah 1</Badge>
                  <CardTitle className="text-xl text-primary">Identitas Peserta</CardTitle>
                  <CardDescription>Lengkapi identitas peserta sebelum mengerjakan 90 pasangan pernyataan.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4">
                  {(assignment.papiDefinition?.identityFields ?? []).map(field => (
                    <div key={field.key} className="grid gap-2">
                      <Label htmlFor={field.key}>
                        {field.label}
                        {field.required ? " *" : ""}
                      </Label>
                      {field.type === "single_choice" ? (
                        <select
                          id={field.key}
                          className="field-select"
                          value={profile[field.key] ?? ""}
                          onChange={event => setProfile(current => ({ ...current, [field.key]: event.target.value }))}
                        >
                          <option value="">Pilih</option>
                          {(field.options ?? []).map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          id={field.key}
                          type={field.type === "number" ? "number" : field.type}
                          value={profile[field.key] ?? ""}
                          onChange={event => setProfile(current => ({ ...current, [field.key]: event.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              currentQuestion && (
                <Card key={currentQuestion.id} className="rounded-lg">
                  <CardHeader>
                    <Badge>
                      Pasangan {currentQuestion.itemNumber} dari {questions.length}
                    </Badge>
                    <CardTitle className="text-lg leading-7 text-primary">{currentQuestion.prompt}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AssessmentQuestionField
                      question={currentQuestion}
                      value={answers[currentQuestion.id]}
                      onChange={next => setAnswers(current => ({ ...current, [currentQuestion.id]: next }))}
                    />
                  </CardContent>
                </Card>
              )
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              {currentPage > 0 ? (
                <Button type="button" variant="outline" onClick={previousStep}>
                  <ArrowLeft />
                  Sebelumnya
                </Button>
              ) : (
                <span />
              )}
              {currentPage < totalQuestionPages ? (
                <Button type="button" onClick={nextStep}>
                  Lanjut
                  <ArrowRight />
                </Button>
              ) : (
                <Button type="submit">
                  Kirim jawaban
                  <ArrowRight />
                </Button>
              )}
            </div>
          </form>
        ) : (
          <form onSubmit={submitGeneric} className="grid gap-5">
            {questions.map((question, index) => (
              <Card key={question.id} className="rounded-lg">
                <CardHeader>
                  <Badge>Pertanyaan {index + 1}</Badge>
                  <CardTitle className="text-lg leading-7 text-primary">{question.prompt}</CardTitle>
                </CardHeader>
                <CardContent>
                  <AssessmentQuestionField
                    question={question}
                    value={answers[question.id]}
                    onChange={next => setAnswers(current => ({ ...current, [question.id]: next }))}
                  />
                </CardContent>
              </Card>
            ))}
            <Button type="submit" size="lg">
              Kirim jawaban
              <ArrowRight />
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}

function serializePapiDraft(profile: Record<string, string>, answers: Record<string, string>) {
  const answerEntries = Object.entries(answers)
    .filter(([, value]) => value === "a" || value === "b")
    .sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify({ profile, answers: answerEntries });
}

function buildPapiDraftPayload(profile: Record<string, string>, answers: Record<string, string>) {
  return {
    respondentProfile: {
      fullName: profile.fullName.trim() || undefined,
      birthDate: profile.birthDate || undefined,
      gender: profile.gender || undefined,
      education: profile.education || undefined,
      position: profile.position || undefined,
      industry: profile.industry || undefined,
      industryOther: profile.industryOther.trim() || undefined,
      tenureMonths: profile.tenureMonths ? Number(profile.tenureMonths) : undefined,
    },
    answers: Object.entries(answers)
      .filter(([, value]) => value === "a" || value === "b")
      .map(([questionId, value]) => ({ questionId, value: value as "a" | "b" })),
  };
}

function buildPapiSubmitProfile(profile: Record<string, string>) {
  return {
    fullName: profile.fullName.trim(),
    birthDate: profile.birthDate,
    gender: profile.gender || undefined,
    education: profile.education,
    position: profile.position,
    industry: profile.industry,
    industryOther: profile.industry === "Lainnya" ? profile.industryOther.trim() : undefined,
    tenureMonths: Number(profile.tenureMonths),
  };
}

function validatePapiIdentity(profile: Record<string, string>) {
  if (!profile.fullName.trim()) return "Nama peserta wajib diisi";
  if (!profile.birthDate) return "Tanggal lahir wajib diisi";
  if (!profile.education) return "Pendidikan terakhir wajib dipilih";
  if (!profile.position) return "Jabatan/posisi wajib dipilih";
  if (!profile.industry) return "Tipe industri wajib dipilih";
  if (profile.industry === "Lainnya" && !profile.industryOther.trim()) return "Jenis industri lainnya wajib diisi";
  if (!profile.tenureMonths || Number.isNaN(Number(profile.tenureMonths))) return "Lama bekerja wajib diisi dalam bulan";
  return null;
}

function formatRemainingTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
